/**
 * Canonical country lookup for surveillance adapters.
 *
 * Closes a whole class of silent-drop bugs: different sources spell countries
 * differently ("USA" / "United States" / "United States of America" / "US";
 * "Korea, Republic of" / "South Korea" / "Republic of Korea"; "Türkiye" /
 * "Turkey"; "Cabo Verde" / "Cape Verde"; etc.). Every adapter that places by
 * country should go through canonicalCountry() so we get one ISO3 + one
 * canonical English name + one centroid, regardless of source spelling.
 *
 * Accepts either an ISO3 (uppercase 3-letter) or a name/alias (case-insensitive
 * with whitespace + punctuation normalised). Returns null for unknown — adapters
 * SKIP (never guess), and the unknown surfaces as a coverage gap rather than
 * silently misplaced data.
 *
 * Adding a country / alias as new sources surface them is a one-line append
 * each; this file is the single source of truth.
 */

export interface CanonicalCountry {
  iso3: string;
  /** Canonical English short name (UI label). */
  name: string;
  /** Approximate national centroid [lat, lon]. */
  centroid: [number, number];
}

/** Primary table, keyed by ISO3. ~170 countries — extend as sources require. */
const CANONICAL: Record<string, CanonicalCountry> = {
  // Africa
  DZA: { iso3: 'DZA', name: 'Algeria',                   centroid: [28.0, 2.6] },
  AGO: { iso3: 'AGO', name: 'Angola',                    centroid: [-11.2, 17.9] },
  BEN: { iso3: 'BEN', name: 'Benin',                     centroid: [9.3, 2.3] },
  BWA: { iso3: 'BWA', name: 'Botswana',                  centroid: [-22.3, 24.7] },
  BFA: { iso3: 'BFA', name: 'Burkina Faso',              centroid: [12.2, -1.6] },
  BDI: { iso3: 'BDI', name: 'Burundi',                   centroid: [-3.4, 29.9] },
  CPV: { iso3: 'CPV', name: 'Cabo Verde',                centroid: [15.1, -23.6] },
  CMR: { iso3: 'CMR', name: 'Cameroon',                  centroid: [5.7, 12.7] },
  CAF: { iso3: 'CAF', name: 'Central African Republic',  centroid: [6.6, 20.9] },
  TCD: { iso3: 'TCD', name: 'Chad',                      centroid: [15.4, 18.7] },
  COM: { iso3: 'COM', name: 'Comoros',                   centroid: [-11.9, 43.9] },
  COG: { iso3: 'COG', name: 'Congo',                     centroid: [-0.7, 15.8] },
  COD: { iso3: 'COD', name: 'Democratic Republic of the Congo', centroid: [-4.0, 21.8] },
  CIV: { iso3: 'CIV', name: "Côte d'Ivoire",             centroid: [7.5, -5.5] },
  DJI: { iso3: 'DJI', name: 'Djibouti',                  centroid: [11.8, 42.6] },
  EGY: { iso3: 'EGY', name: 'Egypt',                     centroid: [26.8, 30.8] },
  GNQ: { iso3: 'GNQ', name: 'Equatorial Guinea',         centroid: [1.6, 10.3] },
  ERI: { iso3: 'ERI', name: 'Eritrea',                   centroid: [15.2, 39.8] },
  SWZ: { iso3: 'SWZ', name: 'Eswatini',                  centroid: [-26.5, 31.5] },
  ETH: { iso3: 'ETH', name: 'Ethiopia',                  centroid: [9.1, 40.5] },
  GAB: { iso3: 'GAB', name: 'Gabon',                     centroid: [-0.8, 11.6] },
  GMB: { iso3: 'GMB', name: 'Gambia',                    centroid: [13.4, -15.5] },
  GHA: { iso3: 'GHA', name: 'Ghana',                     centroid: [7.95, -1.03] },
  GIN: { iso3: 'GIN', name: 'Guinea',                    centroid: [9.9, -9.7] },
  GNB: { iso3: 'GNB', name: 'Guinea-Bissau',             centroid: [12.0, -15.0] },
  KEN: { iso3: 'KEN', name: 'Kenya',                     centroid: [0.0, 37.9] },
  LSO: { iso3: 'LSO', name: 'Lesotho',                   centroid: [-29.6, 28.2] },
  LBR: { iso3: 'LBR', name: 'Liberia',                   centroid: [6.4, -9.4] },
  LBY: { iso3: 'LBY', name: 'Libya',                     centroid: [26.3, 17.2] },
  MDG: { iso3: 'MDG', name: 'Madagascar',                centroid: [-18.8, 46.9] },
  MWI: { iso3: 'MWI', name: 'Malawi',                    centroid: [-13.3, 34.3] },
  MLI: { iso3: 'MLI', name: 'Mali',                      centroid: [17.6, -4.0] },
  MRT: { iso3: 'MRT', name: 'Mauritania',                centroid: [21.0, -10.9] },
  MUS: { iso3: 'MUS', name: 'Mauritius',                 centroid: [-20.3, 57.6] },
  MAR: { iso3: 'MAR', name: 'Morocco',                   centroid: [31.8, -7.1] },
  MOZ: { iso3: 'MOZ', name: 'Mozambique',                centroid: [-18.7, 35.5] },
  NAM: { iso3: 'NAM', name: 'Namibia',                   centroid: [-22.0, 18.5] },
  NER: { iso3: 'NER', name: 'Niger',                     centroid: [17.6, 8.1] },
  NGA: { iso3: 'NGA', name: 'Nigeria',                   centroid: [9.1, 8.7] },
  RWA: { iso3: 'RWA', name: 'Rwanda',                    centroid: [-2.0, 29.9] },
  SEN: { iso3: 'SEN', name: 'Senegal',                   centroid: [14.5, -14.5] },
  SLE: { iso3: 'SLE', name: 'Sierra Leone',              centroid: [8.5, -11.8] },
  SOM: { iso3: 'SOM', name: 'Somalia',                   centroid: [5.2, 46.2] },
  ZAF: { iso3: 'ZAF', name: 'South Africa',              centroid: [-30.6, 22.9] },
  SSD: { iso3: 'SSD', name: 'South Sudan',               centroid: [7.9, 30.2] },
  SDN: { iso3: 'SDN', name: 'Sudan',                     centroid: [15.5, 30.2] },
  TZA: { iso3: 'TZA', name: 'Tanzania',                  centroid: [-6.4, 34.9] },
  TGO: { iso3: 'TGO', name: 'Togo',                      centroid: [8.6, 0.8] },
  TUN: { iso3: 'TUN', name: 'Tunisia',                   centroid: [34.0, 9.6] },
  UGA: { iso3: 'UGA', name: 'Uganda',                    centroid: [1.4, 32.3] },
  ZMB: { iso3: 'ZMB', name: 'Zambia',                    centroid: [-13.1, 27.8] },
  ZWE: { iso3: 'ZWE', name: 'Zimbabwe',                  centroid: [-19.0, 29.8] },

  // Americas
  USA: { iso3: 'USA', name: 'United States',             centroid: [39.5, -98.35] },
  CAN: { iso3: 'CAN', name: 'Canada',                    centroid: [56.1, -106.3] },
  MEX: { iso3: 'MEX', name: 'Mexico',                    centroid: [23.6, -102.5] },
  ARG: { iso3: 'ARG', name: 'Argentina',                 centroid: [-38.4, -63.6] },
  BOL: { iso3: 'BOL', name: 'Bolivia',                   centroid: [-16.3, -63.6] },
  BRA: { iso3: 'BRA', name: 'Brazil',                    centroid: [-14.2, -51.9] },
  CHL: { iso3: 'CHL', name: 'Chile',                     centroid: [-35.7, -71.5] },
  COL: { iso3: 'COL', name: 'Colombia',                  centroid: [4.0, -73.0] },
  ECU: { iso3: 'ECU', name: 'Ecuador',                   centroid: [-1.8, -78.2] },
  GUY: { iso3: 'GUY', name: 'Guyana',                    centroid: [4.9, -58.9] },
  PRY: { iso3: 'PRY', name: 'Paraguay',                  centroid: [-23.4, -58.4] },
  PER: { iso3: 'PER', name: 'Peru',                      centroid: [-9.2, -75.0] },
  SUR: { iso3: 'SUR', name: 'Suriname',                  centroid: [3.9, -56.0] },
  URY: { iso3: 'URY', name: 'Uruguay',                   centroid: [-32.5, -55.8] },
  VEN: { iso3: 'VEN', name: 'Venezuela',                 centroid: [6.4, -66.6] },
  BLZ: { iso3: 'BLZ', name: 'Belize',                    centroid: [17.2, -88.5] },
  CRI: { iso3: 'CRI', name: 'Costa Rica',                centroid: [9.7, -83.8] },
  SLV: { iso3: 'SLV', name: 'El Salvador',               centroid: [13.8, -88.9] },
  GTM: { iso3: 'GTM', name: 'Guatemala',                 centroid: [15.7, -90.2] },
  HND: { iso3: 'HND', name: 'Honduras',                  centroid: [14.8, -86.6] },
  NIC: { iso3: 'NIC', name: 'Nicaragua',                 centroid: [12.9, -85.2] },
  PAN: { iso3: 'PAN', name: 'Panama',                    centroid: [8.4, -80.1] },
  CUB: { iso3: 'CUB', name: 'Cuba',                      centroid: [21.5, -77.8] },
  DOM: { iso3: 'DOM', name: 'Dominican Republic',        centroid: [18.7, -70.2] },
  HTI: { iso3: 'HTI', name: 'Haiti',                     centroid: [19.0, -72.3] },
  JAM: { iso3: 'JAM', name: 'Jamaica',                   centroid: [18.1, -77.3] },
  TTO: { iso3: 'TTO', name: 'Trinidad and Tobago',       centroid: [10.4, -61.2] },
  PRI: { iso3: 'PRI', name: 'Puerto Rico',               centroid: [18.2, -66.4] },
  GUF: { iso3: 'GUF', name: 'French Guiana',             centroid: [3.9, -53.1] },

  // Asia
  AFG: { iso3: 'AFG', name: 'Afghanistan',               centroid: [33.9, 67.7] },
  BGD: { iso3: 'BGD', name: 'Bangladesh',                centroid: [23.7, 90.4] },
  BTN: { iso3: 'BTN', name: 'Bhutan',                    centroid: [27.5, 90.4] },
  KHM: { iso3: 'KHM', name: 'Cambodia',                  centroid: [12.6, 104.9] },
  CHN: { iso3: 'CHN', name: 'China',                     centroid: [35.9, 104.2] },
  HKG: { iso3: 'HKG', name: 'Hong Kong',                 centroid: [22.3, 114.2] },
  TWN: { iso3: 'TWN', name: 'Taiwan',                    centroid: [23.7, 121.0] },
  IND: { iso3: 'IND', name: 'India',                     centroid: [22.0, 79.0] },
  IDN: { iso3: 'IDN', name: 'Indonesia',                 centroid: [-2.5, 118.0] },
  IRN: { iso3: 'IRN', name: 'Iran',                      centroid: [32.4, 53.7] },
  IRQ: { iso3: 'IRQ', name: 'Iraq',                      centroid: [33.0, 43.7] },
  ISR: { iso3: 'ISR', name: 'Israel',                    centroid: [31.0, 34.8] },
  JPN: { iso3: 'JPN', name: 'Japan',                     centroid: [36.2, 138.3] },
  JOR: { iso3: 'JOR', name: 'Jordan',                    centroid: [31.2, 36.5] },
  KAZ: { iso3: 'KAZ', name: 'Kazakhstan',                centroid: [48.0, 68.0] },
  KOR: { iso3: 'KOR', name: 'Republic of Korea',         centroid: [36.5, 127.8] },
  PRK: { iso3: 'PRK', name: 'North Korea',               centroid: [40.3, 127.5] },
  KWT: { iso3: 'KWT', name: 'Kuwait',                    centroid: [29.3, 47.5] },
  LAO: { iso3: 'LAO', name: 'Laos',                      centroid: [19.9, 102.5] },
  LBN: { iso3: 'LBN', name: 'Lebanon',                   centroid: [33.9, 35.9] },
  MYS: { iso3: 'MYS', name: 'Malaysia',                  centroid: [4.2, 101.9] },
  MMR: { iso3: 'MMR', name: 'Myanmar',                   centroid: [21.9, 95.9] },
  NPL: { iso3: 'NPL', name: 'Nepal',                     centroid: [28.4, 84.1] },
  OMN: { iso3: 'OMN', name: 'Oman',                      centroid: [21.5, 55.9] },
  PAK: { iso3: 'PAK', name: 'Pakistan',                  centroid: [30.4, 69.3] },
  PHL: { iso3: 'PHL', name: 'Philippines',               centroid: [12.9, 121.8] },
  QAT: { iso3: 'QAT', name: 'Qatar',                     centroid: [25.3, 51.2] },
  SAU: { iso3: 'SAU', name: 'Saudi Arabia',              centroid: [24.0, 45.0] },
  SGP: { iso3: 'SGP', name: 'Singapore',                 centroid: [1.35, 103.8] },
  LKA: { iso3: 'LKA', name: 'Sri Lanka',                 centroid: [7.9, 80.7] },
  SYR: { iso3: 'SYR', name: 'Syria',                     centroid: [35.0, 38.0] },
  TJK: { iso3: 'TJK', name: 'Tajikistan',                centroid: [38.9, 71.3] },
  THA: { iso3: 'THA', name: 'Thailand',                  centroid: [15.0, 101.0] },
  TLS: { iso3: 'TLS', name: 'Timor-Leste',               centroid: [-8.8, 125.7] },
  TUR: { iso3: 'TUR', name: 'Türkiye',                   centroid: [39.0, 35.2] },
  ARE: { iso3: 'ARE', name: 'United Arab Emirates',      centroid: [24.0, 54.0] },
  UZB: { iso3: 'UZB', name: 'Uzbekistan',                centroid: [41.4, 64.6] },
  VNM: { iso3: 'VNM', name: 'Viet Nam',                  centroid: [14.1, 108.3] },
  YEM: { iso3: 'YEM', name: 'Yemen',                     centroid: [15.4, 44.2] },
  AZE: { iso3: 'AZE', name: 'Azerbaijan',                centroid: [40.4, 47.6] },
  ARM: { iso3: 'ARM', name: 'Armenia',                   centroid: [40.1, 45.0] },
  GEO: { iso3: 'GEO', name: 'Georgia',                   centroid: [42.3, 43.4] },
  KGZ: { iso3: 'KGZ', name: 'Kyrgyzstan',                centroid: [41.2, 74.8] },
  TKM: { iso3: 'TKM', name: 'Turkmenistan',              centroid: [38.97, 59.6] },
  PSE: { iso3: 'PSE', name: 'Palestine',                 centroid: [31.95, 35.2] },

  // Europe
  ALB: { iso3: 'ALB', name: 'Albania',                   centroid: [41.2, 20.0] },
  AUT: { iso3: 'AUT', name: 'Austria',                   centroid: [47.6, 14.1] },
  BLR: { iso3: 'BLR', name: 'Belarus',                   centroid: [53.7, 27.95] },
  BEL: { iso3: 'BEL', name: 'Belgium',                   centroid: [50.6, 4.6] },
  BIH: { iso3: 'BIH', name: 'Bosnia and Herzegovina',    centroid: [43.9, 17.7] },
  BGR: { iso3: 'BGR', name: 'Bulgaria',                  centroid: [42.7, 25.5] },
  HRV: { iso3: 'HRV', name: 'Croatia',                   centroid: [45.1, 15.2] },
  CYP: { iso3: 'CYP', name: 'Cyprus',                    centroid: [35.1, 33.4] },
  CZE: { iso3: 'CZE', name: 'Czechia',                   centroid: [49.8, 15.5] },
  DNK: { iso3: 'DNK', name: 'Denmark',                   centroid: [56.0, 10.0] },
  EST: { iso3: 'EST', name: 'Estonia',                   centroid: [58.6, 25.0] },
  FIN: { iso3: 'FIN', name: 'Finland',                   centroid: [64.0, 26.0] },
  FRA: { iso3: 'FRA', name: 'France',                    centroid: [46.6, 2.2] },
  DEU: { iso3: 'DEU', name: 'Germany',                   centroid: [51.2, 10.4] },
  GRC: { iso3: 'GRC', name: 'Greece',                    centroid: [39.1, 22.9] },
  HUN: { iso3: 'HUN', name: 'Hungary',                   centroid: [47.2, 19.5] },
  ISL: { iso3: 'ISL', name: 'Iceland',                   centroid: [64.96, -19.0] },
  IRL: { iso3: 'IRL', name: 'Ireland',                   centroid: [53.2, -8.0] },
  ITA: { iso3: 'ITA', name: 'Italy',                     centroid: [42.8, 12.6] },
  LVA: { iso3: 'LVA', name: 'Latvia',                    centroid: [56.9, 24.6] },
  LTU: { iso3: 'LTU', name: 'Lithuania',                 centroid: [55.2, 23.9] },
  LUX: { iso3: 'LUX', name: 'Luxembourg',                centroid: [49.8, 6.1] },
  MLT: { iso3: 'MLT', name: 'Malta',                     centroid: [35.94, 14.4] },
  MDA: { iso3: 'MDA', name: 'Moldova',                   centroid: [47.4, 28.4] },
  MNE: { iso3: 'MNE', name: 'Montenegro',                centroid: [42.7, 19.4] },
  NLD: { iso3: 'NLD', name: 'Netherlands',               centroid: [52.1, 5.3] },
  MKD: { iso3: 'MKD', name: 'North Macedonia',           centroid: [41.6, 21.7] },
  NOR: { iso3: 'NOR', name: 'Norway',                    centroid: [64.6, 12.0] },
  POL: { iso3: 'POL', name: 'Poland',                    centroid: [52.0, 19.0] },
  PRT: { iso3: 'PRT', name: 'Portugal',                  centroid: [39.5, -8.0] },
  ROU: { iso3: 'ROU', name: 'Romania',                   centroid: [45.9, 24.9] },
  RUS: { iso3: 'RUS', name: 'Russia',                    centroid: [61.5, 105.0] },
  SRB: { iso3: 'SRB', name: 'Serbia',                    centroid: [44.0, 21.0] },
  SVK: { iso3: 'SVK', name: 'Slovakia',                  centroid: [48.7, 19.7] },
  SVN: { iso3: 'SVN', name: 'Slovenia',                  centroid: [46.1, 14.8] },
  ESP: { iso3: 'ESP', name: 'Spain',                     centroid: [40.0, -4.0] },
  SWE: { iso3: 'SWE', name: 'Sweden',                    centroid: [62.2, 15.6] },
  CHE: { iso3: 'CHE', name: 'Switzerland',               centroid: [46.8, 8.2] },
  UKR: { iso3: 'UKR', name: 'Ukraine',                   centroid: [49.0, 32.0] },
  GBR: { iso3: 'GBR', name: 'United Kingdom',            centroid: [54.0, -2.0] },
  XKX: { iso3: 'XKX', name: 'Kosovo',                    centroid: [42.6, 20.9] },

  // Oceania
  AUS: { iso3: 'AUS', name: 'Australia',                 centroid: [-25.3, 133.8] },
  FJI: { iso3: 'FJI', name: 'Fiji',                      centroid: [-17.7, 178.0] },
  NZL: { iso3: 'NZL', name: 'New Zealand',               centroid: [-41.0, 174.0] },
  PNG: { iso3: 'PNG', name: 'Papua New Guinea',          centroid: [-6.3, 143.9] },
  SLB: { iso3: 'SLB', name: 'Solomon Islands',           centroid: [-9.6, 160.2] },
  VUT: { iso3: 'VUT', name: 'Vanuatu',                   centroid: [-15.4, 166.96] },
  WSM: { iso3: 'WSM', name: 'Samoa',                     centroid: [-13.76, -172.1] },
  TON: { iso3: 'TON', name: 'Tonga',                     centroid: [-21.18, -175.2] },

  // French overseas / dependencies frequently in feeds
  REU: { iso3: 'REU', name: 'Réunion',                   centroid: [-21.1, 55.5] },
  MYT: { iso3: 'MYT', name: 'Mayotte',                   centroid: [-12.8, 45.2] },
  NCL: { iso3: 'NCL', name: 'New Caledonia',             centroid: [-21.3, 165.5] },
  PYF: { iso3: 'PYF', name: 'French Polynesia',          centroid: [-17.7, -149.4] },
  GUM: { iso3: 'GUM', name: 'Guam',                      centroid: [13.4, 144.8] },
};

/** Lowercase-normalised alias → ISO3. Extend as new sources surface variants. */
const ALIAS: Record<string, string> = {
  // United States
  'united states': 'USA', 'united states of america': 'USA', 'us': 'USA', 'u.s.': 'USA', 'u.s.a.': 'USA', 'america': 'USA',
  // United Kingdom (note: feeds sometimes use "England" for the whole UK — accept the conflation)
  'united kingdom': 'GBR', 'uk': 'GBR', 'u.k.': 'GBR', 'great britain': 'GBR', 'britain': 'GBR', 'england': 'GBR',
  // Korea
  'south korea': 'KOR', 'korea, republic of': 'KOR', 'korea, south': 'KOR', 'republic of korea': 'KOR',
  'north korea': 'PRK', "democratic people's republic of korea": 'PRK', 'korea, democratic people\'s republic of': 'PRK', 'dprk': 'PRK',
  // DR Congo / Congo
  'democratic republic of congo': 'COD', 'democratic republic of the congo': 'COD', 'dr congo': 'COD', 'drc': 'COD',
  'congo, democratic republic of the': 'COD', 'congo (kinshasa)': 'COD',
  'republic of the congo': 'COG', 'congo, republic of': 'COG', 'congo-brazzaville': 'COG', 'congo (brazzaville)': 'COG',
  // Iran / Russia / Venezuela / Bolivia / Tanzania / Türkiye / Czechia / Cabo Verde / Eswatini / Macedonia / Burma / Ivory Coast
  'iran, islamic republic of': 'IRN', 'islamic republic of iran': 'IRN',
  'russian federation': 'RUS',
  'venezuela, bolivarian republic of': 'VEN', 'bolivarian republic of venezuela': 'VEN',
  'bolivia (plurinational state of)': 'BOL', 'plurinational state of bolivia': 'BOL',
  'tanzania, united republic of': 'TZA', 'united republic of tanzania': 'TZA',
  'turkey': 'TUR', 'turkiye': 'TUR',
  'czech republic': 'CZE',
  'cape verde': 'CPV',
  'swaziland': 'SWZ',
  'macedonia': 'MKD', 'the former yugoslav republic of macedonia': 'MKD', 'fyr macedonia': 'MKD',
  'burma': 'MMR',
  'ivory coast': 'CIV', "cote d'ivoire": 'CIV',
  // Viet Nam / Laos / Timor-Leste / Palestine / Syria / Moldova / Hong Kong / Taiwan / Brunei / Macao
  'vietnam': 'VNM',
  "lao people's democratic republic": 'LAO', 'lao pdr': 'LAO',
  'east timor': 'TLS', 'timor leste': 'TLS',
  'palestinian territory': 'PSE', 'state of palestine': 'PSE',
  'syrian arab republic': 'SYR',
  'republic of moldova': 'MDA',
  'hong kong sar': 'HKG', 'hong kong sar, china': 'HKG',
  'taiwan, province of china': 'TWN', 'chinese taipei': 'TWN',
  'brunei darussalam': 'BRN',
  'macao': 'MAC', 'macau': 'MAC',
  // UAE / Saudi / etc
  'uae': 'ARE',
  'kingdom of saudi arabia': 'SAU',
  // Reunion accent
  'reunion': 'REU',
};

// Build a name → ISO3 reverse index for the canonical names too (so "Germany" works without an explicit alias).
const _nameIndex: Record<string, string> = {};
for (const [iso, c] of Object.entries(CANONICAL)) _nameIndex[normalise(c.name)] = iso;
for (const [k, v] of Object.entries(ALIAS)) _nameIndex[normalise(k)] = v;

function normalise(s: string): string {
  return s.toLowerCase().trim().replace(/[._,'’`]+/g, ' ').replace(/\s+/g, ' ');
}

/** Resolve a country name or ISO3 to its canonical record. Returns null on miss
 *  (caller SKIPS — never guesses). Accepts: 'USA', 'United States', 'us', etc. */
export function canonicalCountry(input: string | null | undefined): CanonicalCountry | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Direct ISO3 (uppercase 3-letter).
  const up = trimmed.toUpperCase();
  if (up.length === 3 && CANONICAL[up]) return CANONICAL[up]!;
  // Name/alias lookup (normalised, case-insensitive).
  const iso = _nameIndex[normalise(trimmed)];
  return iso ? (CANONICAL[iso] ?? null) : null;
}

/** Iterable of all canonical countries (for legends, coverage views). */
export const CANONICAL_COUNTRIES: readonly CanonicalCountry[] = Object.values(CANONICAL);
